import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

import { formatLengthToArchitecturalString } from '@paragon/client-tools';
import { getLumberDescription } from '@paragon/contract-library';
import { NumericField } from '@paragon/material';
import { InlineNotification } from '@paragon/notification-tools';

import {
  getMissingLumberPriceRowsForActiveComponent,
  getMissingOptimizerPricesStatusForActiveComponent,
} from '../../../../store/selectors/missingLumberPriceSelectors';
import {
  MissingLumberPriceRow,
  resetMissingLumberPricesToDefaults,
  setBracingPrice,
  setMissingLumberPrice,
} from '../../../../store/slices/optimizationSettingsSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { CostTermRow } from './CostTermRow';
import { missingDefaultPriceHighlightSx, MissingDefaultPriceWarning } from './MissingDefaultPriceWarning';

function isAtDefault(row: MissingLumberPriceRow): boolean {
  return row.price === row.defaultPricePerThousandBoardFeet;
}

export function LumberFactors({ disabled }: { readonly disabled: boolean }) {
  const dispatch = useAppDispatch();
  const status = useAppSelector(getMissingOptimizerPricesStatusForActiveComponent);
  const missingLumberPrices = useAppSelector(getMissingLumberPriceRowsForActiveComponent);
  const bracingPrice = useAppSelector((state) => state.optimizationSettings.bracingPrice);

  if (status === 'error') {
    return (
      <InlineNotification type="warning">
        Couldn&apos;t load your organization&apos;s price gaps. The optimizer will use Paragon&apos;s default prices for
        this run.
      </InlineNotification>
    );
  }

  if (status !== 'loaded') return null;

  return (
    <Stack spacing={1}>
      <MissingLumberPrices rows={missingLumberPrices} disabled={disabled} />
      <CostTermRow
        label="Bracing"
        disabled={disabled}
        enabled={bracingPrice.enabled}
        value={bracingPrice.value}
        onToggle={(enabled) => dispatch(setBracingPrice({ ...bracingPrice, enabled }))}
        onValueChange={(value) => dispatch(setBracingPrice({ ...bracingPrice, value }))}
        valueAriaLabel="Bracing cost"
      />
    </Stack>
  );
}

function MissingLumberPrices({
  rows,
  disabled,
}: {
  readonly rows: readonly MissingLumberPriceRow[];
  readonly disabled: boolean;
}) {
  const dispatch = useAppDispatch();
  const anyEdited = rows.some((row) => !isAtDefault(row));

  if (rows.length === 0) return null;

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="subtitle1">Missing lumber prices</Typography>
        {anyEdited && (
          <Button
            size="small"
            variant="text"
            disabled={disabled}
            onClick={() => dispatch(resetMissingLumberPricesToDefaults())}
          >
            Reset to defaults
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary">
        Some lumber the optimizer could use for this truss isn&apos;t priced for your organization. Keep Paragon&apos;s
        default or enter your own for this run. Priced by dollars per thousand board feet.
      </Typography>

      {rows.map((row, index) => {
        const description = getLumberDescription(row.lumber);
        const missingDefault = row.defaultPricePerThousandBoardFeet == null;
        const showMissingDefaultWarning = missingDefault && row.price == null;
        const tag = isAtDefault(row) && !missingDefault ? 'default' : null;

        return (
          <Stack
            key={`${description} ${row.stockLengthInInches}`}
            spacing={0.25}
            sx={showMissingDefaultWarning ? missingDefaultPriceHighlightSx : undefined}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body1" noWrap title={description}>
                  {description}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatLengthToArchitecturalString(row.stockLengthInInches)}
                  {tag != null && ` · ${tag}`}
                </Typography>
              </Box>
              <NumericField<true>
                value={row.price}
                canBeNull
                nullPlaceholder="—"
                onSubmit={(price) => {
                  dispatch(setMissingLumberPrice({ index, price }));
                }}
                min={0}
                numberOfDecimals={2}
                disabled={disabled}
                fullWidth={false}
                startAdornment="$"
                sx={{ width: 96 }}
                slotProps={{ htmlInput: { 'aria-label': `${description} price per 1,000 board feet` } }}
              />
            </Box>
            {showMissingDefaultWarning && <MissingDefaultPriceWarning />}
          </Stack>
        );
      })}
    </Stack>
  );
}
